import {makeAutoObservable} from 'mobx';

import {i18n} from '@yourcoach/i18n';

import type {MembershipApiModel} from '../../../api/membership';
import type {PaymentExpandedCommon} from '../../../api/payment';
import type {
  CoreClientMembershipsListParams,
  CoreClientPaymentsListParams,
} from '../../../api/types/core/methods';
import type {CollectionStore} from '../../../api/utils/collectionStore';
import type {TextInput} from '../../../uikit/TextInput';
import type {Destructible, WithBusyManager} from '../../../utils/store';
import type {GetComponentProps} from '../../../utils/utility-types';

import {membershipStore} from '../../../api/membership';
import {createCollectionStore} from '../../../api/utils/collectionStore';
import {COMMAND} from '../../../services/entity-action/utils';
import {dateTimeObjToInt} from '../../../utils/datetime';
import {envManager} from '../../../utils/env';
import {emitter} from '../../../utils/eventEmitter';
import {eventPayloadActionCreator} from '../../../utils/events';
import {logger} from '../../../utils/logger';
import {
  Currency,
  getPriceString,
  parseAmountString,
} from '../../../utils/money';
import {AsyncBusyManager} from '../../../utils/store';
import {createTextInputField} from '../../../utils/validation';
import {MIN_REFUND_AMOUNT} from '../../payments/utils';
import {REQUEST_REFUND_CREATED_EVENT} from '../constants';

type ReasonKey = 'fetchMembership' | 'fetchPayments' | 'requestRefund';

export class RequestRefundStore
  implements Destructible, WithBusyManager<ReasonKey>
{
  readonly busy = new AsyncBusyManager<ReasonKey>();
  currency = Currency.USD;
  readonly fields = this.makeFields();
  maxAmount = 0;
  requestedAmount = 0;
  private membership?: MembershipApiModel;
  private readonly membershipsStore: CollectionStore<
    MembershipApiModel,
    CoreClientMembershipsListParams
  >;
  private readonly paymentsStore: CollectionStore<
    PaymentExpandedCommon,
    CoreClientPaymentsListParams
  >;

  constructor(courseId: string) {
    this.paymentsStore = createCollectionStore({
      method: 'client.payments.list',
      params: {
        query: [['course_id', '==', courseId]],
      },
    });

    this.membershipsStore = createCollectionStore({
      method: 'client.memberships.list',
      params: {
        query: [
          ['course_id', '==', courseId],
          [
            'status',
            '!in',
            [
              'proposed',
              'requested',
              envManager.isCore ? 'declined' : null,
            ].filter(Boolean) as MembershipApiModel['status'][],
          ],
        ],
      },
    });

    this.busy.observe<RequestRefundStore, 'fetchMembership' | 'fetchPayments'>(
      this,
      {
        fetchMembership: 'fetchMembership',
        fetchPayments: 'fetchPayments',
      },
    );

    this.busy.register('requestRefund');

    makeAutoObservable(this, undefined, {
      autoBind: true,
    });

    this.fetchPayments();
    this.fetchMembership();
  }

  destruct() {
    this.paymentsStore.clear();
    this.membershipsStore.clear();
  }

  get isFetching() {
    return (
      this.busy.getReason('fetchMembership') ||
      this.busy.getReason('fetchPayments')
    );
  }

  get isFormValid() {
    return this.fields.requestedAmount.validate();
  }

  get isRequestRefundInProcess() {
    return this.busy.getReason('requestRefund');
  }

  get maxAmountString() {
    const maxAmount = this.busy.getReason('fetchPayments')
      ? '...'
      : getPriceString({
          currency: this.currency,
          intZeroPrice: true,
          price: this.maxAmount,
        });

    return `${i18n.t('shared:max_amount')}: ${maxAmount}`;
  }

  get requestedAmountInputValue() {
    return !this.fields.requestedAmount.value
      ? ''
      : `${this.currency.sign}${this.fields.requestedAmount.value}`;
  }

  onChangeAmount(
    e: Parameters<GetComponentProps<typeof TextInput>['onChange']>[0],
  ) {
    let amountText = (e.payload?.text || '')
      .replace(this.currency.sign, '')
      .replace(/[^\d.]/g, '');

    amountText = parseAmountString(amountText);

    this.requestedAmount = amountText
      ? (parseFloat(amountText.replace(',', '.')) * 1000) / 10
      : 0;

    this.fields.requestedAmount.onChange!(
      eventPayloadActionCreator({text: amountText}, e.event),
    );
  }

  async requestRefund() {
    if (!this.isFormValid) {
      return COMMAND.CANCEL;
    }

    if (!this.membership) {
      throw new Error(i18n.t('shared:membership_not_found'));
    }

    try {
      this.busy.setReason('requestRefund', true);

      logger.event('request_refund_tap', {reason: 'group_end'});

      await membershipStore.client.requestRefund(this.membership, {
        amount: this.requestedAmount,
      });

      emitter.emit(REQUEST_REFUND_CREATED_EVENT);
    } finally {
      this.busy.setReason('requestRefund', false);
    }
  }

  private calculateMaxAmount() {
    const {items: payments} = this.paymentsStore;

    if (payments.length > 0) {
      const completedPayments: PaymentExpandedCommon[] = [];
      const otherPayments: PaymentExpandedCommon[] = [];

      payments
        .sort(
          (a, b) =>
            dateTimeObjToInt(a.start_date) - dateTimeObjToInt(b.start_date),
        )
        .forEach(payment => {
          if (payment.status === 'paid' || payment.status === 'complete') {
            completedPayments.push(payment);
          } else {
            otherPayments.push(payment);
          }
        });

      if (completedPayments.length > 0) {
        this.maxAmount = completedPayments.reduce((acc, payment) => {
          return acc + payment.amount;
        }, 0);
      } else if (otherPayments.length > 0) {
        this.maxAmount = otherPayments[0]!.amount;
      }

      this.currency = Currency[payments[0]!.currency];
    }
  }

  private async fetchMembership() {
    const memberships = await this.membershipsStore.fetch();

    if (memberships.length > 0) {
      this.membership = memberships[0];
    }
  }

  private async fetchPayments() {
    await this.paymentsStore.fetch();
    this.calculateMaxAmount();
  }

  private makeFields() {
    return {
      requestedAmount: createTextInputField('requestedAmount', {
        customValidate: () => {
          let isValid = true;

          const field = this.fields.requestedAmount;

          field.error = null;

          if (this.requestedAmount < MIN_REFUND_AMOUNT) {
            field.error = i18n.t('shared:min_amount', {
              value: getPriceString({
                currency: this.currency,
                price: MIN_REFUND_AMOUNT,
              }),
            });
            isValid = false;
          } else if (this.requestedAmount > this.maxAmount) {
            field.error = i18n.t('shared:max_amount_max_value', {
              value: getPriceString({
                currency: this.currency,
                intZeroPrice: true,
                price: this.maxAmount,
              }),
            });
            isValid = false;
          }

          field.isValid = isValid;

          return isValid;
        },
        validationRule: {
          messages: {
            required: i18n.t('shared:validation.field_required'),
          },
          numeric: true,
          type: 'string',
        },
      }),
    };
  }
}
